import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye,
  Loader2,
  MousePointerClick,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Mail,
  UserPlus,
  CheckCircle2,
  XCircle,
  Ban,
  Clock,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import type { ScheduledEmailRow } from '../Subscribers/types';
import ScheduleEmailModal from '../Subscribers/ScheduleEmailModal';
import { toast } from 'sonner';
import { loadSendEngagementFlags, type SendEngagementFlag } from '../../lib/emailEngagement';
import { importNewsletterMissingRecipients } from '../../lib/materializeNewsletter';
import { readCustomFieldValue } from '../../lib/chartDefinitions';
import { tierLabel, type ProfileScoreTier } from '../../lib/personalization';
import { subscriberEmailStatus } from '../../lib/subscriberEmailStatus';
import { supabase } from '../../lib/supabase';
import SubscriberHoverCard from '../Common/SubscriberHoverCard';
import AddNewsletterRecipientModal from './AddNewsletterRecipientModal';
import ScheduledEmailStatusModal from './ScheduledEmailStatusModal';

interface NewsletterRecipientsTabProps {
  newsletterId: string;
  newsletterName: string;
  /** Called after a successful purge so the parent can refresh draft state. */
  onPurged?: () => void;
  /** Whether the parent layout allows showing the purge button. */
  canPurge: boolean;
}

type NewsletterRecipientRow = ScheduledEmailRow & {
  postmark_message_id?: string | null;
  subscribers: {
    email: string | null;
    name: string | null;
    profile_score: number | null;
    profile_score_tier: ProfileScoreTier | null;
    email_status: string | null;
    status?: string | null;
    role?: string | null;
    funnel_status?: string | null;
    source?: string | null;
    custom_data?: unknown;
  } | null;
};

function recipientPhotoUrl(customData: unknown): string | null {
  const url = readCustomFieldValue(customData, 'photo_url');
  return url.startsWith('http') ? url : null;
}

function RecipientIdentity({
  email,
  name,
  customData,
}: {
  email: string | null;
  name: string | null;
  customData: unknown;
}) {
  const photo = recipientPhotoUrl(customData);
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      {photo ? (
        <img
          src={photo}
          alt=""
          className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200 dark:border-gray-600"
        />
      ) : null}
      <div className="min-w-0">
        <div className="font-medium truncate underline-offset-2 group-hover:underline">
          {email ?? <span className="text-gray-400">—</span>}
        </div>
        {name ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{name}</div>
        ) : null}
      </div>
    </div>
  );
}

type RecipientSortKey = 'scheduled_at' | 'profile' | 'sent_at' | 'engagement' | 'email_status' | 'status';

type RecipientSortConfig = {
  column: RecipientSortKey;
  direction: 'asc' | 'desc';
};

const DEFAULT_SORT: RecipientSortConfig = { column: 'scheduled_at', direction: 'asc' };

/** Sort on embedded subscriber fields — PostgREST foreignTable order is unreliable. */
const CLIENT_SORT_COLUMNS = new Set<RecipientSortKey>([
  'profile',
  'email_status',
  'engagement',
]);

const SERVER_SORT_COLUMNS = new Set<RecipientSortKey>([
  'scheduled_at',
  'sent_at',
  'status',
]);

interface Counts {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  canceled: number;
  total: number;
}

const PAGE_SIZE = 50;

const STATUS_PILL: Record<string, { label: string; cls: string; Icon: typeof Clock }> = {
  pending: {
    label: 'Pending',
    cls: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200',
    Icon: Clock,
  },
  sending: {
    label: 'Sending',
    cls: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
    Icon: Loader2,
  },
  sent: {
    label: 'Sent',
    cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
    Icon: CheckCircle2,
  },
  failed: {
    label: 'Failed',
    cls: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200',
    Icon: XCircle,
  },
  canceled: {
    label: 'Canceled',
    cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    Icon: Ban,
  },
};

function StatusPill({
  status,
  onClick,
}: {
  status: string;
  onClick?: () => void;
}) {
  const def =
    STATUS_PILL[status] ?? {
      label: status,
      cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
      Icon: Clock,
    };
  const Icon = def.Icon;
  const className = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${def.cls} ${
    onClick
      ? 'hover:ring-2 hover:ring-offset-1 hover:ring-emerald-400/60 dark:hover:ring-offset-gray-900 cursor-pointer'
      : ''
  }`;
  const content = (
    <>
      <Icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
      {def.label}
    </>
  );
  if (!onClick) {
    return <span className={className}>{content}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      title="Open send report and Postmark history"
    >
      {content}
    </button>
  );
}

function CountCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'sky' | 'emerald' | 'rose' | 'gray';
}) {
  const toneCls = {
    sky: 'border-sky-200 dark:border-sky-700 bg-sky-50/60 dark:bg-sky-900/10 text-sky-900 dark:text-sky-100',
    emerald:
      'border-emerald-200 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/10 text-emerald-900 dark:text-emerald-100',
    rose: 'border-rose-200 dark:border-rose-700 bg-rose-50/60 dark:bg-rose-900/10 text-rose-900 dark:text-rose-100',
    gray: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 text-gray-900 dark:text-gray-100',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneCls}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-2xl font-semibold">{value.toLocaleString()}</div>
    </div>
  );
}

function formatTime(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function engagementSortScore(
  flags: SendEngagementFlag | undefined,
  status: string,
): number {
  if (status !== 'sent') return -1;
  if (!flags) return 0;
  if (flags.clicked) return 2;
  if (flags.opened) return 1;
  return 0;
}

function compareSortValues(
  a: string | number | null,
  b: string | number | null,
  direction: 'asc' | 'desc',
): number {
  const emptyA = a == null || a === '';
  const emptyB = b == null || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return direction === 'asc' ? 1 : -1;
  if (emptyB) return direction === 'asc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') {
    const cmp = a - b;
    return direction === 'asc' ? cmp : -cmp;
  }
  const cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return direction === 'asc' ? cmp : -cmp;
}

function sortValueForRecipient(
  row: NewsletterRecipientRow,
  column: RecipientSortKey,
  sendEngagement: Map<string, SendEngagementFlag>,
): string | number | null {
  switch (column) {
    case 'profile': {
      const score = row.subscribers?.profile_score;
      if (score == null || !Number.isFinite(Number(score))) return null;
      return Number(score);
    }
    case 'sent_at':
      return row.sent_at ?? row.scheduled_at ?? null;
    case 'engagement':
      return engagementSortScore(sendEngagement.get(row.id), row.status);
    case 'email_status':
      return subscriberEmailStatus(row.subscribers).toLowerCase() || null;
    case 'status':
      return row.status;
    case 'scheduled_at':
    default:
      return row.scheduled_at;
  }
}

function defaultSortDirection(column: RecipientSortKey): 'asc' | 'desc' {
  switch (column) {
    case 'profile':
    case 'sent_at':
    case 'engagement':
      return 'desc';
    default:
      return 'asc';
  }
}

function SortableTh({
  label,
  column,
  sortConfig,
  onSort,
  title,
  className = '',
}: {
  label: string;
  column: RecipientSortKey;
  sortConfig: RecipientSortConfig;
  onSort: (column: RecipientSortKey) => void;
  title?: string;
  className?: string;
}) {
  const active = sortConfig.column === column;
  const Icon = active && sortConfig.direction === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th className={`text-left px-4 py-2 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        title={title}
        className={`inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 ${
          active ? 'text-gray-700 dark:text-gray-200' : ''
        }`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? '' : 'opacity-30'}`} />
      </button>
    </th>
  );
}

function EmailStatusPill({ row }: { row: NewsletterRecipientRow['subscribers'] }) {
  const status = subscriberEmailStatus(row);
  if (!status) return <span className="text-gray-400">—</span>;
  return (
    <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 capitalize">
      {status}
    </span>
  );
}

export default function NewsletterRecipientsTab({
  newsletterId,
  newsletterName,
  onPurged,
  canPurge,
}: NewsletterRecipientsTabProps) {
  const [counts, setCounts] = useState<Counts>({
    pending: 0,
    sending: 0,
    sent: 0,
    failed: 0,
    canceled: 0,
    total: 0,
  });
  const [rows, setRows] = useState<NewsletterRecipientRow[]>([]);
  const [editingRow, setEditingRow] = useState<NewsletterRecipientRow | null>(null);
  const [statusReportRow, setStatusReportRow] = useState<NewsletterRecipientRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [purging, setPurging] = useState(false);
  const [missingCount, setMissingCount] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [addRecipientOpen, setAddRecipientOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<RecipientSortConfig>(DEFAULT_SORT);
  const [sendEngagement, setSendEngagement] = useState<Map<string, SendEngagementFlag>>(
    () => new Map(),
  );
  const [recipientsRealtime, setRecipientsRealtime] = useState(false);

  const loadCounts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_emails')
        .select('status')
        .eq('newsletter_id', newsletterId);
      if (error) throw error;
      const next: Counts = { pending: 0, sending: 0, sent: 0, failed: 0, canceled: 0, total: 0 };
      for (const r of (data ?? []) as { status: string }[]) {
        next.total += 1;
        const key = r.status as keyof Counts;
        if (key in next && key !== 'total') next[key] += 1;
      }
      setCounts(next);
    } catch (e) {
      console.warn('loadCounts:', e instanceof Error ? e.message : e);
    }
  }, [newsletterId]);

  const loadMissingCount = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('count_newsletter_missing_recipients', {
        p_newsletter_id: newsletterId,
      });
      if (error) throw error;
      setMissingCount(typeof data === 'number' ? data : 0);
    } catch (e) {
      console.warn('loadMissingCount:', e instanceof Error ? e.message : e);
      setMissingCount(null);
    }
  }, [newsletterId]);

  const loadRows = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const usesClientSort = CLIENT_SORT_COLUMNS.has(sortConfig.column);
      const serverSort = SERVER_SORT_COLUMNS.has(sortConfig.column)
        ? sortConfig
        : DEFAULT_SORT;

      let query = supabase
        .from('scheduled_emails')
        .select(
          '*, subscribers(email, name, profile_score, profile_score_tier, email_status, role, funnel_status, source, custom_data)',
        )
        .eq('newsletter_id', newsletterId);

      const ascending = serverSort.direction === 'asc';
      if (serverSort.column === 'sent_at') {
        query = query.order('sent_at', { ascending, nullsFirst: ascending });
      } else if (serverSort.column === 'status') {
        query = query.order('status', { ascending });
      } else {
        query = query.order('scheduled_at', { ascending });
      }

      query = query.order('id', { ascending: true });

      if (!usesClientSort) {
        query = query.range(from, to);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRows((data ?? []) as unknown as NewsletterRecipientRow[]);
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Load failed');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [newsletterId, page, sortConfig]);

  useEffect(() => {
    void loadCounts();
    void loadMissingCount();
  }, [loadCounts, loadMissingCount]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const refreshLive = useCallback(() => {
    void loadCounts();
    void loadMissingCount();
    void loadRows({ silent: true });
  }, [loadCounts, loadMissingCount, loadRows]);

  const refreshLiveRef = useRef(refreshLive);
  useEffect(() => {
    refreshLiveRef.current = refreshLive;
  }, [refreshLive]);

  // Live updates: Supabase realtime on scheduled_emails for this newsletter.
  useEffect(() => {
    setRecipientsRealtime(false);
    const channel = supabase
      .channel(`scheduled_emails:newsletter:${newsletterId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scheduled_emails',
          filter: `newsletter_id=eq.${newsletterId}`,
        },
        () => {
          refreshLiveRef.current();
        },
      )
      .subscribe((status) => {
        setRecipientsRealtime(status === 'SUBSCRIBED');
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [newsletterId]);

  const inFlightCount = counts.pending + counts.sending;

  // Polling while sends are active (and slower while tracking engagement on sent rows).
  useEffect(() => {
    const needsFastPoll = inFlightCount > 0;
    const needsSlowPoll = counts.sent > 0;
    if (!needsFastPoll && !needsSlowPoll) return;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshLiveRef.current();
    };

    const intervalMs = needsFastPoll
      ? recipientsRealtime
        ? 15_000
        : 8_000
      : recipientsRealtime
        ? 45_000
        : 30_000;

    const handle = window.setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [newsletterId, inFlightCount, counts.sent, recipientsRealtime]);

  const loadEngagementFlags = useCallback(async () => {
    if (counts.sent === 0) {
      setSendEngagement(new Map());
      return;
    }
    try {
      setSendEngagement(await loadSendEngagementFlags(newsletterId));
    } catch (e) {
      console.warn('loadEngagementFlags:', e instanceof Error ? e.message : e);
    }
  }, [newsletterId, counts.sent]);

  useEffect(() => {
    void loadEngagementFlags();
  }, [loadEngagementFlags]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q
      ? rows.filter((r) => {
          const email = (r.subscribers?.email ?? '').toLowerCase();
          const name = (r.subscribers?.name ?? '').toLowerCase();
          return email.includes(q) || name.includes(q);
        })
      : [...rows];

    if (CLIENT_SORT_COLUMNS.has(sortConfig.column)) {
      list = [...list].sort((a, b) =>
        compareSortValues(
          sortValueForRecipient(a, sortConfig.column, sendEngagement),
          sortValueForRecipient(b, sortConfig.column, sendEngagement),
          sortConfig.direction,
        ),
      );
    }

    return list;
  }, [rows, query, sortConfig, sendEngagement]);

  const toggleSort = (column: RecipientSortKey) => {
    setSortConfig((prev) => {
      if (prev.column !== column) {
        return { column, direction: defaultSortDirection(column) };
      }
      return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
    setPage(0);
  };

  const handlePurge = async () => {
    if (
      !window.confirm(
        'Delete every pending scheduled send for this newsletter and revert to Draft?',
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      const { data, error } = await supabase.rpc('purge_newsletter_pending_sends', {
        p_newsletter_id: newsletterId,
      });
      if (error) throw error;
      const removed = typeof data === 'number' ? data : 0;
      toast.success(removed > 0 ? `Purged ${removed} pending send(s)` : 'Nothing pending');
      await loadCounts();
      await loadRows();
      onPurged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const handleImportMissing = async () => {
    let available = missingCount;
    if (available == null) {
      try {
        const { data, error } = await supabase.rpc('count_newsletter_missing_recipients', {
          p_newsletter_id: newsletterId,
        });
        if (error) throw error;
        available = typeof data === 'number' ? data : 0;
        setMissingCount(available);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Could not check import count');
        return;
      }
    }

    const countLabel =
      available > 0
        ? `${available.toLocaleString()} new connection${available === 1 ? '' : 's'}`
        : 'eligible connections not yet on this list';

    const contentNote =
      counts.total > 0
        ? 'They will receive the same email content as existing recipients.'
        : 'They will receive the current newsletter content as rendered now.';

    if (
      !window.confirm(
        `Import ${countLabel} as pending scheduled sends? ${contentNote} Already-listed connections are skipped.`,
      )
    ) {
      return;
    }

    setImporting(true);
    try {
      const inserted = await importNewsletterMissingRecipients(newsletterId);

      if (inserted > 0) {
        toast.success(
          `Imported ${inserted} new recipient${inserted === 1 ? '' : 's'} as pending sends`,
        );
      } else {
        toast.message('No new recipients to import');
      }
      await loadCounts();
      await loadMissingCount();
      await loadRows();
      onPurged?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(counts.total / PAGE_SIZE));

  const refreshAll = () => {
    void loadCounts();
    void loadMissingCount();
    void loadRows();
    void loadEngagementFlags();
  };

  const canShowImport = true;
  const importButtonLabel = (() => {
    if (importing) return 'Importing…';
    if (missingCount == null) return 'Import new connections';
    if (missingCount > 0) {
      return `Import new connections (${missingCount.toLocaleString()})`;
    }
    return 'Import new connections';
  })();

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <CountCard label="Pending" value={counts.pending + counts.sending} tone="sky" />
        <CountCard label="Sent" value={counts.sent} tone="emerald" />
        <CountCard label="Failed" value={counts.failed} tone="rose" />
        <CountCard label="Canceled" value={counts.canceled} tone="gray" />
        <CountCard label="Total" value={counts.total} tone="gray" />
      </div>

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search recipients by email or name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshAll}
            title={recipientsRealtime ? 'Refresh (list updates live)' : 'Refresh'}
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {recipientsRealtime ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
              title="Recipient list updates automatically when sends change"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
              Live
            </span>
          ) : null}
          {canShowImport ? (
            <button
              type="button"
              onClick={() => void handleImportMissing()}
              disabled={importing}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
              title="Add confirmed or CRM connections in this newsletter's audience who are not on the recipient list yet"
            >
              {importing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <UserPlus className="w-3.5 h-3.5" />
              )}
              {importButtonLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setAddRecipientOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Search and add one connection manually"
          >
            <Search className="w-3.5 h-3.5" />
            Add person
          </button>
          {canPurge && counts.pending + counts.sending > 0 ? (
            <button
              type="button"
              onClick={() => void handlePurge()}
              disabled={purging}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
            >
              {purging ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              {purging ? 'Purging…' : 'Purge scheduled'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 dark:text-red-300">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
            <Mail className="w-6 h-6 mx-auto mb-2 opacity-50" />
            No recipients yet. Open <span className="font-medium">Actions</span> and choose{' '}
            <span className="font-medium">Queue recipients</span> or{' '}
            <span className="font-medium">Send now</span> to materialize this list.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Recipient</th>
                <SortableTh
                  label="Profile"
                  column="profile"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                  className="w-24"
                  title="Sort by profile score"
                />
                <SortableTh
                  label="Email status"
                  column="email_status"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                  title="Subscriber email status (confirmed, pending, etc.)"
                />
                <SortableTh
                  label="Status"
                  column="status"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                  title="Scheduled send status"
                />
                <SortableTh
                  label="Sent at"
                  column="sent_at"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                  title="Sort by sent time (unsent rows use scheduled time)"
                />
                <SortableTh
                  label="Engagement"
                  column="engagement"
                  sortConfig={sortConfig}
                  onSort={toggleSort}
                  title="Sort by open/click (clicked → opened → none; loads full list)"
                />
                <th className="text-left px-4 py-2 font-medium">Error</th>
                <th className="text-right px-4 py-2 font-medium w-10">
                  <span className="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((r) => (
                <tr key={r.id} className="text-gray-800 dark:text-gray-200">
                  <td className="px-4 py-2 max-w-[320px]">
                    <SubscriberHoverCard
                      subscriberId={r.subscriber_id}
                      seed={{
                        email: r.subscribers?.email,
                        name: r.subscribers?.name,
                        role: r.subscribers?.role,
                        email_status: r.subscribers?.email_status,
                        funnel_status: r.subscribers?.funnel_status,
                        source: r.subscribers?.source,
                        profile_score: r.subscribers?.profile_score,
                        profile_score_tier: r.subscribers?.profile_score_tier,
                        custom_data: r.subscribers?.custom_data,
                      }}
                      className="block w-full"
                    >
                      {r.subscriber_id ? (
                        <Link
                          to={`/subscribers/${r.subscriber_id}`}
                          className="group block hover:text-emerald-600 dark:hover:text-emerald-400"
                          title="Hover for profile · click to open"
                        >
                          <RecipientIdentity
                            email={r.subscribers?.email ?? null}
                            name={r.subscribers?.name ?? null}
                            customData={r.subscribers?.custom_data}
                          />
                        </Link>
                      ) : (
                        <RecipientIdentity
                          email={r.subscribers?.email ?? null}
                          name={r.subscribers?.name ?? null}
                          customData={r.subscribers?.custom_data}
                        />
                      )}
                    </SubscriberHoverCard>
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap">
                    {r.subscribers?.profile_score != null ? (
                      <span title="Profile score for AI personalization">
                        {r.subscribers.profile_score}
                        {r.subscribers.profile_score_tier
                          ? ` · ${tierLabel(r.subscribers.profile_score_tier)}`
                          : ''}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <EmailStatusPill row={r.subscribers} />
                  </td>
                  <td className="px-4 py-2">
                    <StatusPill status={r.status} onClick={() => setStatusReportRow(r)} />
                    {r.attempts > 1 ? (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {r.attempts} attempts
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {formatTime(r.sent_at) === '—'
                      ? `scheduled ${formatTime(r.scheduled_at)}`
                      : formatTime(r.sent_at)}
                  </td>
                  <td className="px-4 py-2">
                    {r.status === 'sent' ? (
                      <RecipientEngagementBadges flags={sendEngagement.get(r.id)} />
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-rose-600 dark:text-rose-400 max-w-[260px] truncate" title={r.error ?? undefined}>
                    {r.error ?? ''}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setEditingRow(r)}
                      className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                      title="Edit scheduled email"
                      aria-label={`Edit scheduled email for ${r.subscribers?.email ?? 'recipient'}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {CLIENT_SORT_COLUMNS.has(sortConfig.column) && counts.total > PAGE_SIZE ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {sortConfig.column === 'profile'
            ? 'Profile score sort'
            : sortConfig.column === 'email_status'
              ? 'Email status sort'
              : 'Engagement sort'}{' '}
          shows all {counts.total.toLocaleString()} recipients (pagination paused).
        </div>
      ) : null}

      {counts.total > PAGE_SIZE && !CLIENT_SORT_COLUMNS.has(sortConfig.column) ? (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div>
            Page {page + 1} of {totalPages} · {counts.total.toLocaleString()} total
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      <AddNewsletterRecipientModal
        isOpen={addRecipientOpen}
        onClose={() => setAddRecipientOpen(false)}
        newsletterId={newsletterId}
        newsletterName={newsletterName}
        onSuccess={() => {
          refreshAll();
          onPurged?.();
        }}
      />

      {editingRow ? (
        <ScheduleEmailModal
          isOpen
          onClose={() => setEditingRow(null)}
          subscriberId={editingRow.subscriber_id}
          subscriberEmail={editingRow.subscribers?.email}
          existing={editingRow}
          onSuccess={() => {
            setEditingRow(null);
            refreshAll();
            onPurged?.();
          }}
        />
      ) : null}

      <ScheduledEmailStatusModal
        isOpen={statusReportRow != null}
        row={statusReportRow}
        onClose={() => setStatusReportRow(null)}
      />
    </div>
  );
}

function RecipientEngagementBadges({ flags }: { flags: SendEngagementFlag | undefined }) {
  if (!flags?.opened && !flags?.clicked) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      {flags.opened ? (
        <span
          className="inline-flex items-center gap-0.5 text-sky-600 dark:text-sky-400"
          title="Opened"
        >
          <Eye className="w-3.5 h-3.5" />
        </span>
      ) : null}
      {flags.clicked ? (
        <span
          className="inline-flex items-center gap-0.5 text-violet-600 dark:text-violet-400"
          title="Clicked a link"
        >
          <MousePointerClick className="w-3.5 h-3.5" />
        </span>
      ) : null}
    </span>
  );
}
